import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  Fade,
  Backdrop,
  Modal,
  Button,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Videocam as VideoIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import VideoViewer from './VideoViewer';

interface VideoMessageProps {
  videoUrl: string;
  videoFilename?: string;
  videoSize?: number;
  videoDuration?: number;
  videoWidth?: number;
  videoHeight?: number;
  videoThumbnailUrl?: string;
  content: string;
  isOwnMessage: boolean;
  timestamp: string;
  senderName: string;
}

const VideoMessage: React.FC<VideoMessageProps> = ({
  videoUrl,
  videoFilename,
  videoSize,
  videoDuration,
  videoWidth,
  videoHeight,
  videoThumbnailUrl,
  content,
  isOwnMessage,
  timestamp,
  senderName,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [videoViewerOpen, setVideoViewerOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const handleDownload = async () => {
    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = videoFilename || 'video';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading video:', error);
    }
  };

  const handlePreview = () => {
    setVideoViewerOpen(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getAspectRatio = (): string => {
    if (videoWidth && videoHeight) {
      const ratio = videoWidth / videoHeight;
      if (ratio > 1.5) return '16:9';
      if (ratio > 1.3) return '4:3';
      if (ratio > 1.1) return '5:4';
      return '1:1';
    }
    return '16:9';
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Paper
          sx={{
            p: 2,
            borderRadius: 3,
            background: isOwnMessage
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            maxWidth: '400px',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {/* Video Thumbnail/Preview */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '200px',
              borderRadius: 2,
              overflow: 'hidden',
              background: '#000',
              cursor: 'pointer',
              mb: 2,
            }}
            onClick={handlePreview}
          >
            <video
              src={videoUrl}
              poster={videoThumbnailUrl}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              preload="metadata"
              muted={muted}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
            
            {/* Play/Pause Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                opacity: playing ? 0 : 1,
                transition: 'opacity 0.3s ease',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.8)',
                }
              }}
            >
              {playing ? <PauseIcon sx={{ fontSize: 30 }} /> : <PlayIcon sx={{ fontSize: 30 }} />}
            </Box>
            
            {/* Duration Badge */}
            {videoDuration && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                }}
              >
                {formatDuration(videoDuration)}
              </Box>
            )}
            
            {/* Fullscreen Button */}
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                opacity: 0,
                transition: 'opacity 0.3s ease',
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              <Tooltip title="Open in Fullscreen">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview();
                  }}
                  sx={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.8)',
                    }
                  }}
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Video Information */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <VideoIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" fontWeight="bold">
                {videoFilename || 'Video'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Chip
                label="Video File"
                size="small"
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontSize: '0.7rem',
                }}
              />
              {videoSize && (
                <Chip
                  label={formatFileSize(videoSize)}
                  size="small"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '0.7rem',
                  }}
                />
              )}
              {videoWidth && videoHeight && (
                <Chip
                  label={`${videoWidth}×${videoHeight}`}
                  size="small"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '0.7rem',
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Content/Description */}
          {content && (
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                opacity: 0.9,
                lineHeight: 1.4,
              }}
            >
              {content}
            </Typography>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              opacity: 0,
              transition: 'opacity 0.2s ease',
              '&:hover': {
                opacity: 1,
              },
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Preview Video">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview();
                  }}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Download Video">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {timestamp}
            </Typography>
          </Box>
        </Paper>
      </motion.div>

      {/* Video Viewer Modal */}
      <VideoViewer
        open={videoViewerOpen}
        onClose={() => setVideoViewerOpen(false)}
        videoUrl={videoUrl}
        fileName={videoFilename || 'video'}
        fileSize={videoSize}
        duration={videoDuration}
        width={videoWidth}
        height={videoHeight}
        thumbnailUrl={videoThumbnailUrl}
      />
    </>
  );
};

export default VideoMessage;
